// Client plumbing: API calls, app-wide signals, routing, formatting.
import { signal } from '@preact/signals';
import type { AppConfig } from '../shared/schemas.ts';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export async function api<T>(path: string, opts: { method?: string; body?: unknown } = {}): Promise<T> {
  const res = await fetch(path, {
    method: opts.method ?? 'GET',
    headers: opts.body !== undefined ? { 'Content-Type': 'application/json' } : undefined,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    credentials: 'same-origin',
  });
  if (res.status === 204) return undefined as T;
  const data = (res.headers.get('content-type') || '').includes('json') ? await res.json() : null;
  // RFC 9457 problem details: prefer the human-readable detail.
  if (!res.ok) throw new ApiError(res.status, data?.detail || data?.title || `Request failed (${res.status})`);
  return data as T;
}

export const config = signal<AppConfig | null>(null);
export const route = signal(location.pathname);
export const toastMsg = signal<string | null>(null);

let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(msg: string) {
  toastMsg.value = msg;
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => {
    toastMsg.value = null;
  }, 2800);
}
export const fail = (e: unknown) => toast(e instanceof Error ? e.message : String(e));

export function navigate(path: string) {
  history.pushState({}, '', path);
  route.value = location.pathname;
  window.scrollTo(0, 0);
}
window.addEventListener('popstate', () => {
  route.value = location.pathname;
});

// ---- local storage (per-viewer conveniences only; the server holds the real state) ----
const safe = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn();
  } catch {
    return fallback;
  }
};
export const savedName = {
  get: () => safe(() => localStorage.getItem('midway:name') || '', ''),
  set: (n: string) => safe(() => localStorage.setItem('midway:name', n), undefined),
};
export const savedMode = {
  get: () => safe(() => localStorage.getItem('midway:mode'), null) as 'foot' | 'bike' | 'car' | 'transit' | null,
  set: (m: string) => safe(() => localStorage.setItem('midway:mode', m), undefined),
};

// ---- formatting ----
export function mins(s: number | null | undefined) {
  if (s == null) return '–';
  const m = Math.max(1, Math.round(s / 60));
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)} h${m % 60 ? ` ${m % 60}` : ''}`;
}
export const initials = (n: string) =>
  n
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

const MODE_EMOJI: Record<string, string> = { foot: '🚶', bike: '🚲', car: '🚗', transit: '🚇' };
export const modeEmoji = (m: string) => config.value?.modes.find((x) => x.id === m)?.emoji ?? MODE_EMOJI[m] ?? '';
